// Direct, not via @workspace/ui/lib/icons: `optimizePackageImports` matches on
// the import specifier and can't see through that re-export. Build-speed only —
// it does NOT shrink the bundle while gen-ui's namespace import retains the full
// icon set (tdd-0074).
import { MapPin, Loader2 } from "lucide-react";
import { cn } from "../../utils";
import type { GeolocationStatus } from "../../hooks/use-geolocation";

/**
 * Inline consent prompt, shown when the model calls a tool that needs to know
 * where the user is.
 *
 * This exists for two reasons, one ethical and one mechanical:
 *
 * - The user sees *why* their location is being asked for, immediately after
 *   the request that prompted it. A bare browser dialog on page load has no
 *   such context and gets reflexively dismissed — permanently, since a denial
 *   is sticky and cannot be re-prompted.
 * - The click IS the user gesture that `getCurrentPosition` needs. Firing it
 *   straight from the tool-call event would have no gesture, which Safari
 *   rejects and Chrome silently denies.
 *
 * Renders nothing once resolved — a chip that lingers after "Share" reads as
 * though something is still pending.
 */

export interface LocationConsentProps {
  /** The tool that triggered the request, for the explanatory line. */
  toolName: string;
  /**
   * Explicit reason, overriding the per-tool lookup.
   *
   * The lookup covers the tools we know about, but a model rendering a
   * `LocationRequest` writes its own reason for a purpose no table can
   * anticipate. Falls back to the table, then to generic copy.
   */
  reason?: string;
  status: GeolocationStatus;
  error?: string | null;
  onShare: () => void;
  onDecline: () => void;
  className?: string;
}

/** Human-readable reason per tool — vague copy defeats the point of asking. */
const REASONS: Record<string, string> = {
  "live-data__weather": "to get the forecast where you are",
  "live-data__air_quality": "to check air quality near you",
  "live-data__sunrise_sunset": "to get sunrise and sunset times for your area",
  "live-data__earthquakes": "to find recent earthquakes near you",
  "live-data__tfl_stops": "to find transport stops near you",
  "live-data__tfl_arrivals": "to find arrivals at your nearest stop",
};

export function LocationConsent({
  toolName,
  reason: reasonOverride,
  status,
  error,
  onShare,
  onDecline,
  className,
}: LocationConsentProps) {
  // Nothing useful to say once the user has decided or the browser can't help.
  if (status === "granted" || status === "unsupported") return null;

  const reason =
    reasonOverride?.trim() || REASONS[toolName] || "to answer that accurately";
  const isPrompting = status === "prompting";
  const isDenied = status === "denied";
  const isError = status === "error";

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border bg-muted/30 p-3 text-sm",
        className,
      )}
    >
      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        {isDenied ? (
          // A denial is sticky — the browser will not re-prompt from here, so
          // saying "try again" would be a lie. Point at the only thing that
          // actually works.
          <p className="text-muted-foreground">
            Location is blocked for this site. You can re-enable it in your
            browser&rsquo;s site settings — until then, tell me a place and
            I&rsquo;ll use that.
          </p>
        ) : isError ? (
          <p className="text-muted-foreground">
            Couldn&rsquo;t get your location{error ? `: ${error}` : ""}. Tell me
            a place and I&rsquo;ll use that instead.
          </p>
        ) : (
          <>
            <p>
              Use your location {reason}?
            </p>
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                onClick={onShare}
                disabled={isPrompting}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {isPrompting && <Loader2 className="h-3 w-3 animate-spin" />}
                {isPrompting ? "Waiting…" : "Share location"}
              </button>
              <button
                type="button"
                onClick={onDecline}
                disabled={isPrompting}
                className="rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                Not now
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
